import React, { useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Flame, BookOpen, LogOut, Zap, Menu, X } from "lucide-react";
import { beltForXP, BeltStripe } from "@/components/BeltSystem";
import AuthModal from "@/components/AuthModal";
import { FestiveBirthdayHat } from "@/lib/america250";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [authOpen, setAuthOpen] = React.useState(false);
  const [authMode, setAuthMode] = React.useState("signin");
  const [menuOpen, setMenuOpen] = React.useState(false);

  const openAuth = (mode = "signin") => { setAuthMode(mode); setAuthOpen(true); setMenuOpen(false); };

  // Close mobile menu on route change. Defensive cleanup for browser back /
  // programmatic navigation; all click-handlers also close the sheet directly.
  useEffect(() => {
    if (menuOpen) setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while sheet is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [menuOpen]);

  const onLogout = async () => { await logout(); setMenuOpen(false); navigate("/"); };

  const { current } = user ? beltForXP(user.xp || 0) : { current: null };

  return (
    <>
    <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />

    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-[var(--line)] safe-top"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
        <Link
          to={user ? "/dashboard" : "/"}
          data-testid="header-logo-link"
          className="flex items-center min-h-[44px] gap-2.5 group flex-shrink-0"
        >
          <div className="relative w-9 h-9 transition-transform group-hover:rotate-3 group-hover:scale-105">
            <img src="/icons/promptdojo-mark.png?v=4" alt="PromptDojo" width={36} height={36} className="w-9 h-9 select-none" draggable="false" />
            {/* America 250 — tasteful birthday hat overlay, only during the campaign */}
            <FestiveBirthdayHat
              size={20}
              style={{
                position: "absolute",
                top: -12,
                left: -6,
                transform: "rotate(-22deg)",
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.25))",
                pointerEvents: "none",
              }}
            />
          </div>
          <span className="font-display text-xl sm:text-2xl tracking-tight leading-none">PromptDojo</span>
        </Link>

        {/* ── Desktop nav (≥ md) ───────────────────────────────────────── */}
        <nav className="hidden md:flex items-center gap-1 md:gap-2">
          {user ? (
            <>
              <div
                className="flex items-center gap-1.5 pill stat-chip"
                data-testid="header-streak"
                title="Daily streak"
                style={{ background: "var(--streak-soft)", borderColor: "rgba(245,158,11,0.3)", color: "var(--gold)" }}
              >
                <Flame size={13} className="flame" />
                <span className="font-bold">{user.streak || 0}</span>
              </div>
              <Link to="/dashboard" className="inline-flex items-center gap-2 pill stat-chip" title={`${current.name} Belt — ${current.title}`} data-testid="header-belt">
                <BeltStripe belt={current} size="sm" />
                <span className="font-bold text-[var(--ink)]">{current.name} Belt</span>
              </Link>
              <Link to="/leaderboard" data-testid="header-leaderboard-link" className="inline-flex items-center min-h-[44px] text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] px-3 py-2 transition-colors">Leaderboard</Link>
              <Link to="/library" data-testid="header-library-link" className="inline-flex items-center min-h-[44px] gap-1 text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] px-3 py-2 transition-colors"><BookOpen size={14} /> Library</Link>
              <Link to="/refer" data-testid="header-refer-link" className="inline-flex items-center min-h-[44px] gap-1 text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] px-3 py-2 transition-colors">Refer &amp; earn</Link>
              {user.is_admin && (
                <Link to="/admin" data-testid="header-admin-link" className="inline-flex items-center min-h-[44px] gap-1 text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] px-3 py-2 transition-colors">Admin</Link>
              )}
              {(user.credits_balance ?? 0) > 0 && (
                <Link to="/power-boosts" data-testid="header-credits" title="AI Power credits" className="inline-flex items-center gap-1.5 pill stat-chip hover:border-[var(--ink)] transition-colors" style={{ background: "rgba(251,191,36,0.10)", borderColor: "rgba(245,158,11,0.30)", color: "#A16207" }}>
                  <Zap size={12} strokeWidth={2.5} />
                  <span className="font-bold">{user.credits_balance}</span>
                </Link>
              )}
              {(!user.tier || user.tier === "free") && (
                <Link to="/pricing" data-testid="header-upgrade-link" className="btn-duo inline-flex items-center min-h-[44px]" style={{ padding: "0.6rem 1rem", fontSize: "0.82rem" }}>Continue Journey</Link>
              )}
              <Link to="/settings" data-testid="header-profile-chip" title={user.email} className="flex items-center gap-2 pl-1 pr-3 min-h-[44px] rounded-full border border-[var(--line)] hover:border-[var(--ink)] hover:bg-[var(--bg-2)] transition-all">
                {user.picture ? (
                  <img src={user.picture} alt={user.name || "Profile"} className="w-7 h-7 rounded-full object-cover border border-[var(--line)]" referrerPolicy="no-referrer" data-testid="header-profile-avatar" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[var(--primary)] text-white flex items-center justify-center text-xs font-bold">{(user.name || user.email || "?").charAt(0).toUpperCase()}</div>
                )}
                <span className="text-sm font-semibold text-[var(--ink)] max-w-[140px] truncate" data-testid="header-profile-name">
                  {(user.name?.split(" ")[0] || user.email?.split("@")[0] || "").replace(/\.$/, "")}
                </span>
              </Link>
              <button onClick={onLogout} data-testid="header-logout-button" className="btn-ghost inline-flex items-center justify-center min-h-[44px] min-w-[44px]" style={{ padding: "0.5rem 0.7rem" }} title="Log out">
                <LogOut size={15} />
              </button>
            </>
          ) : (
            <>
              <Link to="/pricing" data-testid="header-pricing-link" className="inline-flex items-center min-h-[44px] text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] px-4 py-2 transition-colors">Pricing</Link>
              <Link to="/business" data-testid="header-business-link" className="inline-flex items-center min-h-[44px] text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] px-4 py-2 transition-colors">For teams</Link>
              <button onClick={() => openAuth("signin")} data-testid="header-login-button" className="inline-flex items-center min-h-[44px] text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] px-3 py-2 transition-colors">Sign in</button>
              <button onClick={() => { setMenuOpen(false); navigate("/skill-check"); }} data-testid="header-signup-button" className="btn-accent inline-flex items-center min-h-[44px]" style={{ padding: "0.65rem 1.1rem", fontSize: "0.88rem" }}>Take Skill Check</button>
            </>
          )}
        </nav>

        {/* ── Mobile compact cluster + hamburger (< md) ─────────────────── */}
        <div className="flex md:hidden items-center gap-2">
          {user ? (
            <>
              {(user.streak || 0) > 0 && (
                <div className="flex items-center gap-1 pill stat-chip" style={{ background: "var(--streak-soft)", borderColor: "rgba(245,158,11,0.3)", color: "var(--gold)", padding: "0.3rem 0.65rem" }} data-testid="mobile-streak">
                  <Flame size={12} className="flame" />
                  <span className="font-bold text-xs">{user.streak}</span>
                </div>
              )}
            </>
          ) : (
            <button onClick={() => { setMenuOpen(false); navigate("/skill-check"); }} data-testid="mobile-signup-button" className="btn-accent inline-flex items-center min-h-[44px] whitespace-nowrap" style={{ padding: "0.55rem 0.9rem", fontSize: "0.82rem" }}>
              Skill Check
            </button>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            data-testid="mobile-menu-toggle"
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-xl border-2 border-[var(--line-strong)] bg-white text-[var(--ink)] transition-all active:scale-95"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </header>

    {/* ── Mobile sheet (full-width overlay below header) ──────────────── */}
    {menuOpen && (
      <>
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/30 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
          data-testid="mobile-menu-backdrop"
        />
        <nav
          className="md:hidden fixed top-16 left-0 right-0 z-40 bg-white border-b border-[var(--line)] shadow-xl safe-bottom max-h-[calc(100vh-4rem)] overflow-y-auto"
          data-testid="mobile-menu-sheet"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
            {user ? (
              <>
                <Link to="/dashboard" data-testid="mobile-dashboard-link" className="flex items-center justify-between min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink)] hover:bg-[var(--bg-2)] rounded-xl">
                  <span>Dashboard</span>
                  {current && (
                    <span className="inline-flex items-center gap-2 text-xs text-[var(--ink-3)]">
                      <BeltStripe belt={current} size="sm" />
                      <span className="font-bold text-[var(--ink)]">{current.name} Belt</span>
                    </span>
                  )}
                </Link>
                <Link to="/leaderboard" data-testid="mobile-leaderboard-link" className="flex items-center min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">Leaderboard</Link>
                <Link to="/library" data-testid="mobile-library-link" className="flex items-center min-h-[52px] gap-2 px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">
                  <BookOpen size={17} /> Library
                </Link>
                <Link to="/refer" data-testid="mobile-refer-link" className="flex items-center min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">Refer &amp; earn</Link>
                <Link to="/power-boosts" data-testid="mobile-power-link" className="flex items-center justify-between min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">
                  <span className="inline-flex items-center gap-2"><Zap size={16} /> Power Boosts</span>
                  {(user.credits_balance ?? 0) > 0 && (
                    <span className="text-xs font-bold" style={{ color: "#A16207" }}>{user.credits_balance} credits</span>
                  )}
                </Link>
                <Link to="/pricing" data-testid="mobile-pricing-link" className="flex items-center min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">Pricing</Link>
                {user.is_admin && (
                  <Link to="/admin" data-testid="mobile-admin-link" className="flex items-center min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">Admin</Link>
                )}
                <div className="h-px bg-[var(--line)] my-2" />
                <Link to="/settings" data-testid="mobile-settings-link" className="flex items-center min-h-[52px] gap-3 px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover border border-[var(--line)]" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[var(--primary)] text-white flex items-center justify-center text-xs font-bold">{(user.name || user.email || "?").charAt(0).toUpperCase()}</div>
                  )}
                  <span>Settings</span>
                </Link>
                <button onClick={onLogout} data-testid="mobile-logout-button" className="flex items-center justify-between min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">
                  <span>Log out</span>
                  <LogOut size={16} />
                </button>
              </>
            ) : (
              <>
                <button onClick={() => openAuth("signin")} data-testid="mobile-signin-button" className="flex items-center min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink)] hover:bg-[var(--bg-2)] rounded-xl text-left">Sign in</button>
                <Link to="/pricing" data-testid="mobile-pricing-link" className="flex items-center min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">Pricing</Link>
                <Link to="/business" data-testid="mobile-business-link" className="flex items-center min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">For teams</Link>
                <Link to="/refer" data-testid="mobile-refer-link" className="flex items-center min-h-[52px] px-3 py-3 text-base font-semibold text-[var(--ink-2)] hover:bg-[var(--bg-2)] rounded-xl">Refer &amp; earn</Link>
                <div className="h-px bg-[var(--line)] my-2" />
                <button onClick={() => { setMenuOpen(false); navigate("/skill-check"); }} data-testid="mobile-signup-cta" className="btn-accent w-full inline-flex items-center justify-center min-h-[52px] mt-1" style={{ fontSize: "0.95rem" }}>
                  Start My Skill Check
                </button>
              </>
            )}
          </div>
        </nav>
      </>
    )}
    </>
  );
}
